import { applyMiddleware, createStore, Store } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import { createLogger } from 'redux-logger';

import { RootActions, rootReducer, RootState } from 'src/reducers';

export let store: Store<RootState, RootActions>;

export const configureStore = (preloadedState: Partial<RootState>) => {
  const middlewares: any[] = [];

  if (process.env.NODE_ENV !== 'production') {
    middlewares.push(createLogger());
  }

  const middlewareEnhancer = applyMiddleware(...middlewares);

  const enhancers = [middlewareEnhancer];
  const composedEnhancers = composeWithDevTools(...enhancers);

  store = createStore(rootReducer, preloadedState, composedEnhancers);

  if (process.env.NODE_ENV !== 'production' && module.hot) {
    module.hot.accept('src/reducers', () => store.replaceReducer(rootReducer));
  }

  return store;
};

/**
 * This is a helper method to set up a listener to the redux store.
 * You pass in a selector function which is used to get the item of the store you care about.
 * The observer will then pay attention to this item and see when it's been changed (by reference for
 * objects and by value for primitives).
 * When there is a change, it will execute the callback and pass it the updated value.
 *
 * You can use this to easily watch for changes in the store outside of react components (which should
 * never need this method since the components can connect to the store via react-redux's connect)
 *
 * @param {func} select Selector function
 * @param {func} onChange Callback to execute when there is a change
 */
export const observeStore = <T>(select: (state: RootState) => T, onChange: (value: T) => void) => {
  let currentState: T;

  const handleChange = () => {
    const nextState = select(store.getState());

    if (nextState !== currentState) {
      currentState = nextState;
      onChange(currentState);
    }
  };

  const unsubscribe = store.subscribe(handleChange);

  handleChange();
  return unsubscribe;
};
